import { SuccessData } from 'types/api';
import { executeApiCall } from 'services/api';
import { getMutations } from 'entities/hooks/mutation';
import { Invoice } from '../types';
import { UploadedFile } from 'types';

export type CreateInvoiceEntryConfig =
  | { type: 'PROJECT'; entries: Array<{ project: string; id: string; type: 'PROJECT' }> }
  | {
      type: 'PACKAGE';
      entries: Array<{ id: string; type: 'PACKAGE'; country: string; value: string }>;
    };

type InvoiceCreationProps = Pick<
  Invoice,
  | 'additionalPercentage'
  | 'dateRequest'
  | 'comment'
  | 'address'
  | 'poNumber'
  | 'description'
  | 'companyName'
  | 'clientPM'
  | 'emails'
  | 'taxId'
> & {
  clientBalances: string[];
} & CreateInvoiceEntryConfig;

export const invoiceEntityMutations = getMutations({
  createInvoice: {
    action: async (payload: InvoiceCreationProps): Promise<Invoice> => {
      return executeApiCall({
        path: '/invoice/invoices',

        method: 'post',

        payload,
      });
    },

    messages: {
      error: (t) => t('failed-to-create-invoice')!,

      success: (t) => t('created-invoice')!,
    },
  },

  cancelInvoice: {
    action: async (payload: { invoice: string; justification: string }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/invoice/invoices/cancel',

        method: 'post',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('invoice-canceled'),
      }),
    },
  },

  requestInvoiceCancelation: {
    action: async (payload: {
      invoice: string;
      justification: string;
    }): Promise<{ requestId: string }> => {
      return executeApiCall({
        path: '/invoice/invoices/cancel/request',

        method: 'post',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('cancelation-requested'),
      }),
    },
  },

  rejectInvoiceCancelation: {
    action: async (payload: { invoice: string; justification: string }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/invoice/invoices/cancel/reject',

        method: 'post',

        payload,
      });
    },
  },

  downloadInvoicesExcel: {
    action: async (payload: { invoices: string[] }): Promise<Buffer> => {
      return executeApiCall({
        path: '/invoice/invoices/accountant-excel',

        method: 'post',

        responseType: 'blob',

        payload,
      });
    },

    messages: {
      error: (t) => ({
        title: t('excel-download-error'),
        type: 'error',
      }),
    },
  },

  sendInvoicesToAccountant: {
    action: async (payload: { invoices: string[]; suppressIds: string[] }): Promise<Buffer> => {
      return executeApiCall({
        path: '/invoice/invoices/send-to-accountant',

        responseType: 'blob',

        payload,
      });
    },

    messages: {
      error: (t) => ({
        title: t('excel-download-error'),
        type: 'error',
      }),
    },
  },

  confirmInvoicePayment: {
    action: async (payload: {
      balanceComment?: string;
      invoice: string;
      valuePaid: string;
      date: string;
      file: UploadedFile;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: 'invoice/invoices/payment',

        method: 'post',

        payload,
      });
    },

    messages: {
      error: (t) => ({
        title: t('invoice-upload-failed'),
        type: 'error',
      }),

      success: (t) => ({
        title: t('invoice-upload-success'),
        type: 'success',
      }),
    },
  },

  uploadInvoiceFile: {
    action: async (payload: {
      accountantId: string;
      netValue: string;
      invoicedAt: string;
      invoice: string;
      files: UploadedFile[];
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: 'invoice/invoices/upload-invoice',

        method: 'post',

        payload,
      });
    },

    messages: {
      error: (t) => ({
        title: t('invoice-upload-failed'),
        type: 'error',
      }),

      success: (t) => ({
        title: t('invoice-upload-success'),
        type: 'success',
      }),
    },
  },
});
